import React, { memo, useCallback } from 'react'
import { UserLink } from 'components/forms/UserLink'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Button } from 'transfix-ui/components/Button'
import { ContextCard } from 'transfix-ui/components/ContextCard'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { ICarrierApplicationStates, Maybe } from 'types/graphqlTypes'
import {
  FMCSARating,
  STATUSES,
} from 'apps/marketplace/pages/shipment-details/components/FmcsaRating'
import { formatDate } from 'utils/formatDatetime'
import { DISPLAY } from 'constants/display'
import CarrierStatus from './CarrierStatus'

interface ICarrierContextCardProps {
  carrierId?: string
  carrierName?: Maybe<string>
  carrierMcNumber?: Maybe<string>
  carrierDotNumber?: Maybe<string>
  carrierFmcsaRating?: Maybe<string>
  carrierHqCity?: Maybe<string>
  carrierHqState?: Maybe<string>
  carrierState?: ICarrierApplicationStates
  pcmName?: Maybe<string>
  pcmParentUserId?: Maybe<string>
  rateConSignedAt?: Maybe<string>
}

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    maxWidth: '350px',
  },
  capitalizeText: {
    textTransform: 'capitalize',
  },
}))

const CarrierContextCard = memo<ICarrierContextCardProps>(
  ({
    carrierDotNumber: dotNumber,
    carrierFmcsaRating: fmcsaRating,
    carrierHqCity: hqCity,
    carrierHqState: hqState,
    carrierId,
    carrierMcNumber: mcNumber,
    carrierName: name,
    carrierState: status,
    pcmName,
    pcmParentUserId,
    rateConSignedAt,
  }) => {
    const classes = useStyles()

    const hq = `${(hqCity || '').toLowerCase()}, ${hqState}`

    const handleContextCardClick = useCallback(() => {
      window.open(`${__ROOT__}/carriers/${carrierId}`, '_blank')
    }, [carrierId])

    return (
      <ContextCard triggerText={name || DISPLAY.empty}>
        <div className={classes.container}>
          <Box mb={3}>
            <Typography variant='h3'>{name || DISPLAY.empty}</Typography>
          </Box>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Status
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <CarrierStatus status={status} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Rating
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2'>
                <FMCSARating status={fmcsaRating as keyof typeof STATUSES} />
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                MC
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2'>{mcNumber || DISPLAY.empty}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                DOT
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2'>{dotNumber || DISPLAY.empty}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                HQ
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.capitalizeText} variant='body2'>
                {hq || DISPLAY.empty}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box py={0.5}>
                <Tooltip
                  position='bottom'
                  type='dark'
                  trigger={
                    <Typography component='span' variant='body2' color='textSecondary'>
                      PCM <InfoIcon />
                    </Typography>
                  }>
                  <Typography variant='caption'>Primary Carrier Manager</Typography>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2'>
                {pcmName ? (
                  <UserLink id={pcmParentUserId || DISPLAY.empty} name={pcmName || DISPLAY.empty} />
                ) : (
                  DISPLAY.empty
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Signed On
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              {rateConSignedAt ? (
                <Typography variant='body2'>
                  {formatDate(rateConSignedAt, 'stopAppointmentMilitaryFormatFull' as any)}
                </Typography>
              ) : (
                DISPLAY.empty
              )}
            </Grid>
          </Grid>
          <Box mt={3}>
            <Button fullWidth onClick={handleContextCardClick}>
              View Carrier Profile
            </Button>
          </Box>
        </div>
      </ContextCard>
    )
  }
)

export default CarrierContextCard
